import { RequestOptions } from '../httpClient/types';
import { Branch } from './branchesApi';
import { httpClient } from '../httpClient/httpClient';
import { UserListItem, UserType } from './userApi';
import { MatrixAxis } from './compositionGroup/compositionGroupMatrixApi';
import { IdNameDto } from './sharedTypes';

export interface CreateArticleNodeValue {
  id: number;
  name: string;
}

export interface CreateArticleRequest {
  name: string;
  itemCode: string;
  conceptId: number | null;
  articleTemplateId: number;
  productGroupId: number;
  contactId: number | null;
  branches: { id: number }[];
  variable: boolean;
  collectionItem: boolean;
  branchCertificates: ArticleBranchCertificates[];
  nodeXValues: CreateArticleNodeValue[];
  nodeYValues: CreateArticleNodeValue[];
  colorAxis: number | null;
  layoutFileId: number | null;
  force: boolean;
  masterBranchId: number | null;
}

export enum ArticleState {
  Registered = 0,
  Accepted = 1,
  Rejected = 2,
  OnHold = 3,
  Cancelled = 4,
}

export interface MatrixNodeValue {
  id: number;
  matrixNodeId: number;
  identifier: string;
  name: string | null;
  displayOrder: number;
  archived: boolean;
}

export interface ArticleNodeValue {
  id: number;
  name: string | null;
  matrixNodeValue: MatrixNodeValue;
}

export interface ArticleNodeGroup {
  nodeXValues: ArticleNodeValue[];
  nodeYValues: ArticleNodeValue[];
}

export interface MoveArticle {
  articleId: number;
  itemCode: string;
  brand: string;
  project: string;
  concept: string;
}

export interface UpdateArticle {
  variantId: number;
  name: string;
  itemCode: string;
  branchId: number;
  branchIdentifier: string;
  state: ArticleState;
  categoryCode: string;
}

interface MoveRequest {
  articleIds: number[];
  toCustomerId: number;
  toConceptId: number | null;
}

interface UpdateStateRequest extends UpdateVariants {
  state: ArticleState;
}

interface UpdateCategoryCodeRequest extends UpdateVariants {
  categoryCode: string;
}

interface UpdateVariants {
  variants: UpdateVariant[];
}

export interface UpdateVariant {
  variantId: number;
  branchId: number;
}

export interface ArticleAxisInfo {
  articleId: number;
  variantId: number;
  colorAxis: MatrixAxis | null;
  nodeXValues: ArticleNodeValue[];
  nodeYValues: ArticleNodeValue[];
}

export interface ProductDevelopersResponse {
  all: UserListItem[];
  recommended: RecommendedBranchProductDevelopers[];
}

export interface UpdateProductDevelopersRequest {
  productDevelopers: BranchProductDeveloper[];
}

export interface PreviewInquiryUserChangesRequest {
  changes: InquiryUserChange[];
}

export interface InquiryUserChange {
  branchId: number;
  branchIdentifier: string;
  oldUserId: number | null;
  newUserId: number | null;
}

export interface BranchInquiryUserChangePreview {
  branchId: number;
  branchIdentifier: string;
  inquiries: InquiryUserChangePreview[];
}

export interface InquiryUserChangePreview {
  inquiryId: number;
  inquiryNumber: string;
  currentUserId: number | null;
  currentUserName: string | null;
  currentUserEmail: string | null;
  newUserId: number | null;
  newUserName: string | null;
  newUserEmail: string | null;
}

export interface RecommendedBranchProductDevelopers {
  branchId: number | null;
  branchIdentifier: string | null;
  recommended: UserListItem[];
}

export interface BranchProductDeveloper {
  userId: number | null;
  branchId: number;
  branchIdentifier: string;
  userName: string | null;
  userEmail: string | null;
  updateInquiryUser?: boolean;
}

interface ArticleVariant {
  id: number;
  name: string;
  description: string;
  articleId: number;
  isProductDevelopment: boolean;
}

export interface ValidateItemCodeResponse {
  isValid: boolean;
  errorMessage: string | null;
  errorType: string | null;
}

export interface AddVdcArticleRequest {
  itemCode: string;
  description: string;
  productGroupId: number;
  templateCompositionGroupId: number;
  customerId: number;
  conceptId: number | null;
  branchIds: number[];
  contactId: number | null;
  tierRangeId: number;
  width: number;
  length: number;
  treatmentIds: number[] | null;
}

export interface AddVdcArticleResponse {
  articleId: number;
  itemCode: string;
}

export interface AddVdcOptionRequest {
  variantId: number;
  templateCompositionGroupId: number;
  width: number;
  length: number;
  tierRangeId: number;
  treatmentIds: number[] | null;
}

export interface Article {
  id: number;
  itemCode: string;
  name: string;
  customerId: number;
  customerName: string;
  conceptId: number | null;
  conceptName: string | null;
  basedOnId: number | null;
  basedOnArticleNumber: string | null;
  syncedVariantId: number | null;
  mainVariantId: number | null;
  matrixNodeXId: number;
  matrixNodeYId: number;
  version: number;
  archived: boolean;
  ownerId: number;
  itemCodeMinLength: number | null;
  itemCodeMaxLength: number | null;
  productGroupId: number | null;
  contactId: number | null;
  isVariable: boolean;
  collectionItem: boolean;
  branches: IdNameDto[];
}

export interface UpdateArticleMetadataRequest {
  name: string;
  itemCode: string;
  conceptId: number | null;
  productGroupId: number | null;
  contactId: number | null;
  isVariable: boolean;
  collectionItem: boolean;
  ownerId: number;
}

export interface ArticleLifecycleResponse {
  id: number;
  archived: boolean;
}

export interface ArticleBranchOption {
  id: number;
  name: string;
  identifier: string;
}

export interface UpdateArticleBranchesRequest {
  branchIds: number[];
}

export interface ArticleBranchCertificates {
  branchId: number;
  certificateDefinitionIds: number[];
}

export interface ArticleCertificateBranch extends ArticleBranchCertificates {
  name: string;
  identifier: string;
}

export interface UpdateArticleCertificatesRequest {
  branches: ArticleBranchCertificates[];
}

export interface ArticleUser {
  id: number;
  userId: number;
  name: string;
  email: string;
  userType: UserType | null;
  customUserType: string | null;
  branchId: number | null;
  branchName: string | null;
  branchIdentifier: string | null;
  isMain: boolean;
  inherited: boolean;
  inCustomerTeam: boolean;
}

export interface AddArticleUserRequest {
  userId: number;
  branchId: number | null;
  userType: UserType | null;
  customUserType: string | null;
}

export interface UpdateArticleUserRequest {
  branchId: number | null;
  userType: UserType | null;
  customUserType: string | null;
}

export interface DirectoryArticle {
  id: number;
  itemCode: string;
  name: string;
}

export interface ArticleCopySourceVariant {
  id: number;
  name: string;
  isProductDevelopment: boolean;
  supplierCount: number;
  compositionGroupCount: number;
  inquiryCount: number;
  offerCount: number;
  options: ArticleCopySourceOption[];
  hasPurchaseInformation: boolean;
  hasSalesInformation: boolean;
  hasArtwork: boolean;
  hasPhoto: boolean;
  suppliers: ArticleCopySourceSupplier[];
}

export interface ArticleCopySourceOption {
  id: number;
  version: number;
  name: string;
  linkedVariantId: number | null;
}

export interface ArticleCopySourceSupplierBranch {
  branchId: number;
  versionCount: number;
  inquiryCount: number;
}

export interface ArticleCopySourceSupplier {
  id: number;
  name: string;
  branches: ArticleCopySourceSupplierBranch[];
}

export interface ArticleCopySourceBranch {
  id: number;
  name: string;
  identifier: string;
  isSource: boolean;
}

export interface ArticleCopySource {
  id: number;
  itemCode: string;
  name: string;
  customerId: number;
  customerName: string;
  conceptId: number | null;
  conceptName: string | null;
  itemCodeMinLength: number | null;
  itemCodeMaxLength: number | null;
  contact: ArticleCopySourceContact | null;
  tags: ArticleCopySourceTag[];
  targetTiers: ArticleCopySourceTargetTier[];
  branches: ArticleCopySourceBranch[];
  variants: ArticleCopySourceVariant[];
}

export interface ArticleCopySourceContact {
  id: number;
  name: string;
  email: string;
}

export interface ArticleCopySourceTag {
  id: number;
  label: string;
  color: string;
  archived: boolean;
}

export interface ArticleCopySourceTargetTier {
  quantity: number;
  archived: boolean;
}

export interface ArticleCopyTarget {
  itemCode: string;
  name: string;
}

export interface ArticleCopyVariantOptions {
  variantId: number;
  suppliers: boolean;
  purchaseInformation: boolean;
  salesInformation: boolean;
  artwork: boolean;
  photo: boolean;
  compositionGroups: boolean;
  inquiries: boolean;
  offers: boolean;
  supplierSelections: ArticleCopySupplierSelection[];
  optionIds: number[];
}

export interface ArticleCopySupplierSelection {
  supplierId: number;
  inquiries: boolean;
}

export interface CopyArticleRequest {
  customerId: number;
  conceptId: number | null;
  ignoreClashingItemCodes: boolean;
  branchIds: number[];
  targets: ArticleCopyTarget[];
  article: {
    contact: boolean;
    tags: boolean;
    targetTiers: boolean;
  };
  variants: ArticleCopyVariantOptions[];
}

export interface CopyArticleResponse {
  jobId: number;
  targetCount: number;
}

export const articlesApi = {
  createArticles: (customerId: number, articles: CreateArticleRequest[]) =>
    httpClient.post<{
      jobId: number | null;
      targetCount: number;
      articleId: number | null;
    }>(`articles/customers/${customerId}`, { data: { articles } }),
  getImportBranches: (itemCode: string) =>
    httpClient.get<Branch[]>('articles/import-branches', {
      params: { itemCode },
    }),
  moveArticles: (request: MoveRequest) =>
    httpClient.post<MoveArticle[]>('articles/move', { data: request }),
  updateState: (request: UpdateStateRequest) =>
    httpClient.put<UpdateArticle[]>('articles/state', { data: request }),
  getArticle: (id: number) => httpClient.get<Article>(`articles/${id}`),
  getCertificates: (id: number) =>
    httpClient.get<ArticleCertificateBranch[]>(`articles/${id}/certificates`),
  updateCertificates: (id: number, request: UpdateArticleCertificatesRequest) =>
    httpClient.put(`articles/${id}/certificates`, { data: request }),
  getCopySource: (id: number) =>
    httpClient.get<ArticleCopySource>(`articles/${id}/copy-source`),
  copyArticle: (id: number, request: CopyArticleRequest) =>
    httpClient.post<CopyArticleResponse>(`articles/${id}/copies`, {
      data: request,
    }),
  updateMetadata: (id: number, request: UpdateArticleMetadataRequest) =>
    httpClient.put<Article>(`articles/${id}/metadata`, { data: request }),
  archive: (id: number) =>
    httpClient.put<ArticleLifecycleResponse>(`articles/${id}/archive`),
  restore: (id: number) =>
    httpClient.put<ArticleLifecycleResponse>(`articles/${id}/restore`),
  updateBranches: (id: number, request: UpdateArticleBranchesRequest) =>
    httpClient.put<ArticleBranchOption[]>(`articles/${id}/branches`, {
      data: request,
    }),
  getArticleUsers: (id: number) =>
    httpClient.get<ArticleUser[]>(`articles/${id}/users`),
  addArticleUser: (id: number, request: AddArticleUserRequest) =>
    httpClient.post<number>(`articles/${id}/users`, { data: request }),
  updateArticleUser: (
    id: number,
    articleUserId: number,
    request: UpdateArticleUserRequest,
  ) =>
    httpClient.put(`articles/${id}/users/${articleUserId}`, { data: request }),
  removeArticleUser: (id: number, articleUserId: number) =>
    httpClient.delete(`articles/${id}/users/${articleUserId}`),
  getDirectoryArticles: (conceptId: number | null, customerId: number) =>
    httpClient.get<DirectoryArticle[]>(
      conceptId
        ? `concepts/${conceptId}/articles`
        : `customers/${customerId}/articles`,
    ),
  updateCategoryCode: (request: UpdateCategoryCodeRequest) =>
    httpClient.put<UpdateArticle[]>('articles/category-code', {
      data: request,
    }),
  getUpdateVariants: (request: UpdateVariants) =>
    httpClient.post<UpdateArticle[]>('articles/update', {
      data: request,
    }),
  getMoveArticles: (articleIds: number[]) =>
    httpClient.get<MoveArticle[]>(`articles/move`, {
      params: { articleIds: articleIds },
    }),
  getArticleBranches: (articleId: number) =>
    httpClient.get<ArticleBranchOption[]>(`articles/${articleId}/branches`),
  getArticleSuppliers: (articleId: number) =>
    httpClient.get<IdNameDto[]>(`articles/${articleId}/suppliers`),
  getAxisInfo: (id: number) =>
    httpClient.get<ArticleAxisInfo>(`articles/${id}/matrix/axis`),
  setColorAxis: (id: number, axis: MatrixAxis) =>
    httpClient.put<ArticleAxisInfo>(`articles/${id}/matrix/axis/${axis}`),
  getArticleProductDevelopers: (articleId: number) =>
    httpClient.get<BranchProductDeveloper[]>(
      `articles/${articleId}/product-developers`,
    ),
  getRecommendedProductDevelopers: (articleId: number) =>
    httpClient.get<ProductDevelopersResponse>(
      `articles/${articleId}/recommended/product-developers`,
    ),
  getVariants: (id: number) =>
    httpClient.get<ArticleVariant[]>(`articles/${id}/variants`),
  updateProductDevelopers: (
    articleId: number,
    request: UpdateProductDevelopersRequest,
  ) =>
    httpClient.put<BranchProductDeveloper[]>(
      `articles/${articleId}/product-developers`,
      {
        data: request,
      },
    ),
  previewInquiryUserChanges: (
    articleId: number,
    request: PreviewInquiryUserChangesRequest,
  ) =>
    httpClient.post<BranchInquiryUserChangePreview[]>(
      `articles/${articleId}/preview-inquiry-user-changes`,
      {
        data: request,
      },
    ),
  validateItemCodes: (itemCodes: string[], options?: RequestOptions) =>
    httpClient.post<Partial<Record<string, ValidateItemCodeResponse>>>(
      'articles/validate-item-codes',
      { ...options, data: { itemCodes } },
    ),
  addVdcArticle: (request: AddVdcArticleRequest) =>
    httpClient.post<AddVdcArticleResponse>('articles/vdc', { data: request }),
  addVdcArticleOption: (request: AddVdcOptionRequest) =>
    httpClient.post('articles/vdc/option', {
      data: request,
    }),
  migrateToV2: (articleId: number) =>
    httpClient.post(`articles/${articleId}/migrate-v2`),
  downgradeToV0: (articleId: number) =>
    httpClient.post(`articles/${articleId}/downgrade`),
};
